import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import RichTextEditor from 'components/admin/RichTextEditor';
import { getAdminLeaders } from 'apis/adminLeaders';
import { createLeaderInformationItemsBatch, getAdminLeaderInformationItemById, getAdminLeaderInformationItems, updateLeaderInformationItem } from 'apis/adminLeaderInformationItems';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';

const newItem = (languages, sortOrder = 0) => ({ sortOrder, isActive: true, translation: languages.map(({ id }) => ({ languageId: id, title: '', description: '' })) });
const leaderName = (leader) => leader?.name || leader?.translations?.[0]?.name || `Lãnh đạo ${leader?.id}`;

const getRawTranslations = (detail) => {
    const candidates = [
        detail?.translations,
        detail?.translation,
        detail?.leaderInformationItemTranslations,
        detail?.LeaderInformationItemTranslations,
    ];
    return candidates.find(Array.isArray) || [];
};

const normalizeTranslations = (detail, languages) => {
    const rawTranslations = getRawTranslations(detail);
    const normalized = rawTranslations.map((entry) => {
        const languageCode = entry.languageCode ?? entry.LanguageCode ?? entry.language?.code ?? entry.Languages?.Code;
        const language = languages.find((item) => item.code === languageCode);
        return {
            ...entry,
            languageId: Number(entry.languageId ?? entry.LanguageId ?? language?.id),
            title: entry.title ?? entry.Title ?? '',
            description: entry.description ?? entry.Description ?? '',
        };
    }).filter((entry) => Number.isFinite(entry.languageId));

    return languages.map((language) => {
        const existing = normalized.find((entry) => entry.languageId === Number(language.id));
        return existing || { languageId: language.id, title: '', description: '' };
    });
};

const LeaderInformationItemsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const isEdit = Boolean(id);
    const [tab, setTab] = useState(0);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({ leaderId: '', items: [] });
    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: leaderData = { items: [] } } = useAdminListQuery('leaders-information-options', { pageSize: 1000, languageCode: 'vi', isActive: true }, getAdminLeaders);
    const leaders = useMemo(() => {
        const uniqueById = new Map();
        (leaderData.items || []).forEach((leader) => {
            const key = Number(leader.id);
            if (Number.isFinite(key) && !uniqueById.has(key)) uniqueById.set(key, leader);
        });
        return Array.from(uniqueById.values());
    }, [leaderData.items]);
    const { data: detail, error } = useAdminDetailQuery('leader-information-items', id, getAdminLeaderInformationItemById);
    const createMutation = useAdminCreateMutation('leader-information-items', createLeaderInformationItemsBatch);
    const updateMutation = useAdminUpdateMutation('leader-information-items', updateLeaderInformationItem);

    useEffect(() => {
        if (!id && languages.length && !form.items.length) setForm((old) => ({ ...old, items: [newItem(languages)] }));
    }, [id, languages, form.items.length]);
    useEffect(() => {
        if (!detail || !languages.length) return;
        setForm({
            leaderId: detail.leaderId ?? detail.LeaderId ?? '',
            items: [{
                sortOrder: detail.sortOrder ?? detail.SortOrder ?? 0,
                isActive: detail.isActive ?? detail.IsActive ?? true,
                translation: normalizeTranslations(detail, languages),
            }],
        });
    }, [detail, languages]);
    useEffect(() => { if (error) toast.error(error.message || 'Tải dữ liệu thất bại'); }, [error, toast]);

    const changeLeader = async (value) => {
        setForm((old) => ({ ...old, leaderId: value }));
        if (!value || isEdit) return;
        try {
            const response = await getAdminLeaderInformationItems({ pageIndex: 1, pageSize: 1000, leaderId: value, languageCode: 'vi' });
            const records = Array.isArray(response?.data) ? response.data : response?.data?.items || [];
            const next = records.reduce((max, item) => Math.max(max, Number(item.sortOrder) || 0), 0) + 1;
            setForm((old) => ({ ...old, items: old.items.map((item, index) => ({ ...item, sortOrder: next + index })) }));
        } catch { toast.error('Không lấy được thứ tự tự động'); }
    };
    const changeItem = (index, field, value) => setForm((old) => ({ ...old, items: old.items.map((item, position) => position === index ? { ...item, [field]: value } : item) }));
    const changeTranslation = (itemIndex, languageId, field, value) => setForm((old) => ({ ...old, items: old.items.map((item, position) => {
        if (position !== itemIndex) return item;
        const exists = item.translation.some((translation) => Number(translation.languageId) === Number(languageId));
        const translation = exists
            ? item.translation.map((entry) => Number(entry.languageId) === Number(languageId) ? { ...entry, [field]: value } : entry)
            : [...item.translation, { languageId, title: '', description: '', [field]: value }];
        return { ...item, translation };
    }) }));
    const addItem = () => setForm((old) => ({ ...old, items: [...old.items, newItem(languages, Math.max(0, ...old.items.map((item) => Number(item.sortOrder) || 0)) + 1)] }));
    const removeItem = (index) => setForm((old) => ({ ...old, items: old.items.filter((_, position) => position !== index) }));
    const payload = (item) => ({ leaderId: Number(form.leaderId), sortOrder: Number(item.sortOrder) || 0, isActive: Boolean(item.isActive), translation: item.translation.map((entry) => ({ languageId: Number(entry.languageId), title: entry.title || '', description: entry.description || '' })) });

    const submit = async (event) => {
        event.preventDefault();
        const nextErrors = {};
        if (!form.leaderId) nextErrors.leaderId = 'Vui lòng chọn lãnh đạo';
        form.items.forEach((item, index) => { if (!item.translation.some((entry) => entry.title?.trim())) nextErrors[index] = 'Vui lòng nhập tiêu đề cho ít nhất một ngôn ngữ'; });
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return;
        try {
            const response = isEdit ? await updateMutation.mutateAsync({ id, data: payload(form.items[0]) }) : await createMutation.mutateAsync(form.items.map(payload));
            if (!response.success) return toast.error(response.message || 'Lưu thất bại');
            toast.success(isEdit ? 'Cập nhật thành công' : 'Thêm mới thành công');
            navigate('/admin/leader-information-items');
        } catch { toast.error('Lưu thông tin lãnh đạo thất bại'); }
    };

    const language = languages[tab];
    const loading = createMutation.isPending || updateMutation.isPending;
    return <div className="admin-page">
        <PageHeader title={isEdit ? 'Cập nhật thông tin lãnh đạo' : 'Thêm thông tin lãnh đạo'} subtitle="Liên kết từng phần thông tin với hồ sơ lãnh đạo" />
        <form className="form-card leader-information-form" onSubmit={submit}>
            <p className="form-section-title">Liên kết lãnh đạo</p>
            <Select label="Lãnh đạo *" value={form.leaderId} onChange={(event) => changeLeader(event.target.value)} options={leaders.map((leader) => ({ value: leader.id, label: leaderName(leader) }))} placeholder="Chọn lãnh đạo" error={errors.leaderId} required />
            <p className="form-section-title">Các phần thông tin</p>
            <div className="translation-tabs"><div className="tab-header">{languages.map((item, index) => <button key={item.id} type="button" className={`tab-btn ${tab === index ? 'active' : ''}`} onClick={() => setTab(index)}>{item.name} ({item.code})</button>)}</div></div>
            {form.items.map((item, index) => {
                const translation = item.translation.find((entry) => Number(entry.languageId) === Number(language?.id)) || {};
                return <div className="admin-nested-block information-item-card" key={index}>
                    <div className="information-item-card__header">
                        <div><span className="information-item-card__number">{String(index + 1).padStart(2, '0')}</span><strong>Phần thông tin {index + 1}</strong></div>
                        {!isEdit && <button type="button" className="information-item-card__remove" onClick={() => removeItem(index)} disabled={form.items.length === 1} title="Xóa phần thông tin"><FiTrash2 size={16} /><span>Xóa</span></button>}
                    </div>
                    <div className="information-item-card__settings"><Input label="Thứ tự" type="number" value={item.sortOrder} onChange={(event) => changeItem(index, 'sortOrder', event.target.value)} /><div className="information-item-card__status"><span>Trạng thái</span><Checkbox label="Hoạt động" checked={item.isActive} onChange={(event) => changeItem(index, 'isActive', event.target.checked)} /></div></div>
                    {language && <div className="information-item-card__translation" key={`${index}-${language.id}`}>
                        <Input label={`Tiêu đề (${language.name}) *`} value={translation.title || ''} onChange={(event) => changeTranslation(index, language.id, 'title', event.target.value)} error={errors[index]} />
                        <div className="input-group">
                            <label>Mô tả ({language.name})</label>
                            <RichTextEditor key={`description-${index}-${language.id}`} value={translation.description || ''} onChange={(value) => changeTranslation(index, language.id, 'description', value)} placeholder="Nhập nội dung phần thông tin..." />
                        </div>
                    </div>}
                </div>;
            })}
            {!isEdit && <Button type="button" variant="secondary" className="information-item-add" onClick={addItem}><FiPlus size={16} /> Thêm phần thông tin</Button>}
            <div className="form-actions"><Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : 'Lưu'}</Button><Button type="button" variant="secondary" onClick={() => navigate('/admin/leader-information-items')}>Hủy</Button></div>
        </form>
    </div>;
};

export default LeaderInformationItemsForm;
